// Shared absence-overlap check for PersonaAbsences docs.
// Used by computeMemberAvailability (TaskEditor badges) and the agenda's
// absent-persona styling.

export const intervalsOverlap = (aStart, aEnd, bStart, bEnd) =>
    aStart < bEnd && aEnd > bStart

/**
 * Leest een `StartTime`/`EndTime` van een afwezigheid ('HH:MM', zoals een
 * `<input type="time">` hem schrijft).
 *
 * Alles wat geen echte tijd is — leeg, `undefined`, onzin, 25:00 — leest als
 * 'geen tijd opgegeven'. Dat is ook precies wat elke bestaande rij heeft, en
 * die moeten hele dagen blijven.
 *
 * @param {*} value - De opgeslagen waarde
 * @returns {{hours: number, minutes: number}|null}
 */
const parseTimeOfDay = (value) => {
    if (typeof value !== 'string') return null
    const match = value.trim().match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/)
    if (!match) return null

    const hours = Number(match[1])
    const minutes = Number(match[2])
    if (hours > 23 || minutes > 59) return null

    return { hours, minutes }
}

/** Dezelfde dag, maar op dit tijdstip. */
const atTime = (day, { hours, minutes }) =>
    new Date(day.getFullYear(), day.getMonth(), day.getDate(), hours, minutes)

/**
 * Het tijdvak dat deze afwezigheid beslaat.
 *
 * Datums staan opgeslagen op lokale middernacht van de dag, en `EndDate` telt
 * mee — zonder tijden loopt een afwezigheid dus tot het EINDE van de einddag.
 *
 * `StartTime` en `EndTime` verfijnen alleen die twee randen. Over meer dagen is
 * het daarmee **één aaneengesloten blok** en niet elke dag opnieuw tussen die
 * tijden: 'van maandag 10:00 tot woensdag 14:00' betekent dat de dinsdag er
 * helemaal in valt, net als bij een afspraak. Wie elke ochtend weg is, legt dat
 * vast als losse regels — een herhalend patroon is dit bewust niet.
 *
 * Een leeg of omgekeerd tijdvak (eindtijd vóór de begintijd op dezelfde dag)
 * geeft `null`. Zo'n afwezigheid telt nergens mee; het scherm waarschuwt erover
 * in plaats van hem stil te laten verdwijnen.
 *
 * AI-CONTEXT: See docs/features/afwezigheid-met-tijden.md
 *
 * @param {object} absence - Document uit PersonaAbsences
 * @returns {{start: Date, end: Date}|null}
 */
export const absenceWindow = (absence) => {
    const startDay = absence && absence.StartDate ? absence.StartDate.toDate() : null
    if (!startDay) return null

    const startTime = parseTimeOfDay(absence.StartTime)
    const start = startTime ? atTime(startDay, startTime) : startDay

    let end
    if (!absence.EndDate) {
        end = new Date(8640000000000000) // open-ended (max Date)
    } else {
        const endDay = absence.EndDate.toDate()
        const endTime = parseTimeOfDay(absence.EndTime)
        end = endTime
            ? atTime(endDay, endTime)
            : new Date(endDay.getFullYear(), endDay.getMonth(), endDay.getDate() + 1)
    }

    // Een tijdvak zonder duur bestaat niet. Het stil laten passeren zou een
    // afwezigheid opleveren die nooit ergens mee overlapt — zichtbaar in de
    // tabel, maar zonder enig effect.
    if (end <= start) return null

    return { start, end }
}

/**
 * Of deze afwezigheid binnen één kalenderdag valt.
 *
 * @param {object} absence - Document uit PersonaAbsences
 * @returns {boolean}
 */
const isSingleDay = (absence) => {
    if (!absence || !absence.StartDate || !absence.EndDate) return false
    const a = absence.StartDate.toDate()
    const b = absence.EndDate.toDate()
    return a.getFullYear() === b.getFullYear()
        && a.getMonth() === b.getMonth()
        && a.getDate() === b.getDate()
}

const twoDigits = (n) => String(n).padStart(2, '0')

/**
 * 'HH:MM–HH:MM' voor naast het woord 'Afwezig', of een lege string.
 *
 * Alleen binnen één dag: bij een afwezigheid over meer dagen zou '10:00–14:00'
 * suggereren dat iemand er tussendoor gewoon is, en dat is precies het
 * tegenovergestelde van wat er staat.
 *
 * @param {object} absence - Document uit PersonaAbsences
 * @returns {string}
 */
export const absenceTimeLabel = (absence) => {
    if (!isSingleDay(absence)) return ''
    if (!parseTimeOfDay(absence.StartTime) && !parseTimeOfDay(absence.EndTime)) return ''

    const window = absenceWindow(absence)
    if (!window) return ''

    const { start, end } = window
    const eind = end.getDate() !== start.getDate()
        ? '24:00'
        : `${twoDigits(end.getHours())}:${twoDigits(end.getMinutes())}`

    return `${twoDigits(start.getHours())}:${twoDigits(start.getMinutes())}–${eind}`
}

const findAbsence = (absences, personaId, windowStart, windowEnd) => {
    if (!Array.isArray(absences)) return null
    for (const a of absences) {
        if (a.PersonaID !== personaId) continue
        const w = absenceWindow(a)
        if (!w) continue
        if (intervalsOverlap(windowStart, windowEnd, w.start, w.end)) return a
    }
    return null
}

export default findAbsence
